import React, { FC, useState, useEffect } from 'react';
import {
  DeviceGeneralStatistics,
  Port,
  Sensor,
  SensorVisualizationType,
} from '@thingslog/repositories';
import { useTranslation } from 'react-i18next';
import { Panel, PanelTitle } from './base';
import clsx from 'clsx';
import { InfoRow, InfoRowsViewer } from './base/InfoRowsViewer';
import WaterMeter from '@thingslog/repositories/src/sensor/WaterMeter';
import { SensorUtils } from '@thingslog/utils';
import GenericDigitalSensor from '@thingslog/repositories/src/sensor/GenericDigitalSensor';
import { getSensorUnitForVisualization } from '../utils';

export const SensorStatisticsInfo: FC<Props> = ({
  selectedSensorIndex,
  statistics,
  ports,
  className,
  dataVisualizationType,
}: Props) => {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState<InfoRow[]>([]);

  useEffect(() => {
    const rows: InfoRow[] = [];
    if (ports === undefined || ports[selectedSensorIndex] === undefined) {
      setRows(rows);
      return;
    }
    let sensor: Sensor = ports[selectedSensorIndex].sensor;

    rows.push({ type: 'default', title: `${t('sensor_info_name')}:`, value: `${sensor.name}` });
    rows.push({
      type: 'default',
      title: `${t('sensor_info_type')}:`,
      value: `${t(sensor['@type'])}`,
    });

    if (sensor['@type'] === 'water_meter') {
      let waterMeter: WaterMeter = sensor as WaterMeter;
      const manufacturer = waterMeter.manufacturer;
      const model = waterMeter.model;
      const serialNumber = waterMeter.serialNumber;
      const diameter = waterMeter.diameter;

      if (manufacturer) {
        rows.push({
          type: 'default',
          title: `${t('sensor_info_manufacturer')}:`,
          value: `${manufacturer}`,
        });
      }
      if (model) {
        rows.push({ type: 'default', title: `${t('sensor_info_model')}:`, value: `${model}` });
      }
      if (serialNumber) {
        rows.push({
          type: 'default',
          title: `${t('sensor_info_serial_number')}:`,
          value: `${serialNumber}`,
        });
      }
      if (diameter !== null) {
        rows.push({
          type: 'default',
          title: `${t('sensor_info_diameter')}:`,
          value: `${diameter} mm`,
        });
      }
    }

    if (!SensorUtils({ sensor }).isOnOffSensor()) {
      const unit = getSensorUnitForVisualization(sensor, dataVisualizationType);
      const currRow: InfoRow = {
        type: 'default',
        title: `${t('sensor_info_current')}:`,
        value: `-- ${unit}`,
      };
      const minRow: InfoRow = {
        type: 'default',
        title: `${t('sensor_info_min')}:`,
        value: `-- ${unit}`,
      };
      const maxRow: InfoRow = {
        type: 'default',
        title: `${t('sensor_info_max')}:`,
        value: `-- ${unit}`,
      };
      const avgRow: InfoRow = {
        type: 'default',
        title: `${t('sensor_info_avg')}:`,
        value: `-- ${unit}`,
      };
      // const totalRow: InfoRow = { title: 'Total:', value: `-- ${unit}` };
      const counterRow: InfoRow = {
        type: 'default',
        title: `${t('sensor_info_counters')}:`,
        value: `--`,
      };

      if (statistics !== undefined && statistics.current !== null) {
        currRow.value = `${Number(statistics.current)} ${unit}`;
        minRow.value = `${Number(statistics.min)} ${unit}`;
        maxRow.value = `${Number(statistics.max)} ${unit}`;
        avgRow.value = `${Number(statistics.avg.toFixed(sensor.scale || 2))} ${unit}`;
        counterRow.value = `${statistics.counters}`;
      }

      const isDigitalSensor = SensorUtils({ sensor }).isDigitalSensor();
      const isCumulative = isDigitalSensor
        ? (sensor as GenericDigitalSensor)?.cumulative === true
        : false;
      const hideMinMaxAvg = isDigitalSensor && isCumulative && dataVisualizationType === 'READINGS';

      rows.push(currRow);
      if (!hideMinMaxAvg) {
        rows.push(minRow);
        rows.push(maxRow);
        rows.push(avgRow);
      }
      // rows.push(totalRow);
      rows.push(counterRow);
    }
    setRows(rows);
  }, [ports, selectedSensorIndex, statistics, i18n.language, dataVisualizationType]);

  return (
    <Panel className={clsx('space-y-2.5', className)}>
      <PanelTitle title={t('sensor_info_header')} />
      <InfoRowsViewer rows={rows} />
    </Panel>
  );
};

interface Props {
  selectedSensorIndex: number;
  statistics?: DeviceGeneralStatistics;
  isStatisticsLoading?: boolean;
  ports?: Record<number, Port>;
  className?: string;
  dataVisualizationType: SensorVisualizationType | null;
}
